import React from "react";
import TitleBar from "../../components/navbar/TitleBar";
import SchoolBusNavMenu from "../../components/createForm/SchoolBusNavMenu";
import Footer from "../../components/common/Footer";
import SubTitle from "../../components/common/SubTitle";
import SearchFilter from "../../components/common/SearchFilter";
import { Suspense, useState, useMemo, useEffect, useRef } from "react";
import SchoolDynamicTable from "../../components/createForm/SchoolDynamicTable";
import InactiveInventoryTable from "../../components/common/InactiveInventoryTable";
import CreateDriverForm from "../../components/createForm/CreateDriverForm";
import DriverBehaviorPanel from "../../components/driverSafety/DriverBehaviorPanel";
import axios from "axios";
import config from "../../config.js";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { PAGE_IDS, isJsonDrivenLayoutEnabled } from "../../config/uiLayoutConfig";
import { usePageLayout } from "../../context/LayoutContext";
import { useUser } from "../login/UserContextPersistent";
import { createHasPermission } from "../../layout/navLayoutUtils";
import {
  buildFilterWidgetsFromLayout,
  buildSchoolTableHeadersFromLayout,
  getActionBarWidgets,
  getPageTitleKey,
  getTableFeatures,
} from "../../layout/moduleLayoutUtils";

export default function SchoolBusDrivers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const openedDriverFromReport = useRef(false);
  const scrolledToDriver = useRef(false);
  const { state, organizationInactive } = useUser();
  const hasPermission = useMemo(
    () =>
      createHasPermission(state?.features, {
        writesDisabled: organizationInactive,
      }),
    [state?.features, organizationInactive],
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [filterValues, setFilterValues] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [driversData, setDriversData] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const useJsonLayout = isJsonDrivenLayoutEnabled();
  const { layout: moduleLayout } = usePageLayout(
    useJsonLayout ? PAGE_IDS.SCHOOL_DRIVERS : null,
  );

  const legacyHeaders = [
    { key: "IDs", value: "IDs" },
    { key: "Names", value: "Names" },
    { key: "Emails", value: "Emails" },
    { key: "Phone No.", value: "Phone No." },
    { key: "Vehicles", value: "Vehicles" },
    { key: "DL Numbers", value: "DL Numbers" },
    { key: "actions", value: "" },
  ];

  const headers = useMemo(() => {
    if (moduleLayout) {
      return buildSchoolTableHeadersFromLayout(moduleLayout, { t, hasPermission }) || legacyHeaders;
    }
    return legacyHeaders;
  }, [moduleLayout, t, hasPermission]);

  const legacyFilters = useMemo(
    () => [
      {
        id: "vehicles",
        label: "Vehicles",
        helperText: "Select vehicle",
        options: vehicleOptions,
      },
    ],
    [vehicleOptions],
  );

  const filters = useMemo(() => {
    const layoutFilters = moduleLayout ? buildFilterWidgetsFromLayout(moduleLayout) : null;
    if (!layoutFilters) {
      return legacyFilters;
    }

    return layoutFilters.map((widget) => ({
      id: widget.filterKey,
      label: widget.labelKey,
      helperText: `Select ${String(widget.labelKey || "").toLowerCase()}`,
      options:
        widget.filterKey === "vehicles"
          ? vehicleOptions
          : (widget.options || []).map((option) => ({
              value: option.value,
              label: option.label,
            })),
    }));
  }, [moduleLayout, legacyFilters, vehicleOptions]);

  const tableFeatures = useMemo(() => getTableFeatures(moduleLayout) || {}, [moduleLayout]);
  const actionBarWidgets = useMemo(
    () => (moduleLayout ? getActionBarWidgets(moduleLayout, hasPermission) : []),
    [moduleLayout, hasPermission],
  );
  const createAction = actionBarWidgets.find((widget) => widget.actionKey === "createDriver");
  const pageTitle = getPageTitleKey(moduleLayout) || "Drivers";

  // Fetch drivers data on component mount
  useEffect(() => {
    fetchDrivers();
    fetchVehicles();
  }, []);

  const fetchImage = async (imageUrl) => {
    try {
      const response = await axios.get(imageUrl, {
        responseType: "arraybuffer",
        withCredentials: true,
      });
      const blob = new Blob([response.data], { type: "image/jpeg" });
      const imageDataUrl = URL.createObjectURL(blob);
      return imageDataUrl;
    } catch (error) {
      console.error(`Error fetching image from ${imageUrl}:`, error.message);
      return null;
    }
  };

  const fetchVehicles = async () => {
    try {
      const vehiclesResponse = await axios.get(
        `${config.baseURL}/vehicle/organization`,
        { withCredentials: true },
      );

      if (!Array.isArray(vehiclesResponse.data)) {
        setVehicleOptions([]);
        return;
      }

      const vehicles = vehiclesResponse.data;
      const vehicleOpts = vehicles.map((vehicle) => ({
        value: vehicle.id.toString(),
        label: vehicle.name || vehicle.VehicleNumber || `Vehicle ${vehicle.id}`,
      }));

      setVehicleOptions(vehicleOpts);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    }
  };

  const fetchDrivers = async () => {
    setIsLoading(true);
    try {
      const parsedData = JSON.parse(localStorage.getItem("userState"));
      const orgId = parsedData?.user?.organizationId;

      const response = await axios.get(
        `${config.baseURL}/driver/organization`,
        {
          params: { organizationId: orgId },
          withCredentials: true,
        },
      );

      if (!Array.isArray(response.data)) {
        setDriversData([]);
        setIsLoading(false);
        return;
      }

      const drivers = [...response.data].sort((a, b) => {
        const aTime = new Date(a.createdAt || a.updatedAt || 0).getTime();
        const bTime = new Date(b.createdAt || b.updatedAt || 0).getTime();

        if (aTime !== bTime) {
          return bTime - aTime;
        }

        return Number(b.id || 0) - Number(a.id || 0);
      });

      // Transform driver data with images
      const transformedData = await Promise.all(
        drivers.map(async (driver) => {
          const photoDocument = driver.documents?.find(
            (doc) => doc.name === "DriverImage",
          );
          const photoFileName = photoDocument ? photoDocument.location : null;

          let imageUrl = null;
          if (photoFileName) {
            const imageUrlPath = `${config.baseURL}/upload/image?fileName=${photoFileName}`;
            try {
              imageUrl = await fetchImage(imageUrlPath);
            } catch (error) {
              console.error(
                `Failed to fetch image for driver ${driver.id}:`,
                error,
              );
              imageUrl = null;
            }
          }

          return {
            IDs: driver.driver_id || "N/A",
            Names:
              `${driver.first_name || ""} ${driver.last_name === "." ? "" : driver.last_name || ""}`.trim() ||
              "N/A",
            Emails: driver.email || "N/A",
            "Phone No.": driver.phone_number || "N/A",
            Vehicles: driver.vehicle
              ? driver.vehicle.name ||
                driver.vehicle.device_id ||
                "Not Assigned"
              : "Not Assigned",
            "DL Numbers": driver.driving_license_number || "N/A",
            imageUrl: imageUrl,
            fullData: {
              ...driver,
              id: driver.id,
              driverId: driver.driver_id || "",
              driverName:
                `${driver.first_name || ""} ${driver.last_name === "." ? "" : driver.last_name || ""}`.trim(),
              email: driver.email || "",
              phoneNumber: driver.phone_number || "",
              assignedVehicle: driver.vehicle ? driver.vehicle.id : "",
              dlNumber: driver.driving_license_number || "",
              profileImage: imageUrl,
              presentAddress: driver.current_address || "",
              presentCity: driver.current_city || "",
              presentPincode: driver.current_pincode || "",
              permanentAddress: driver.permanent_address || "",
              permanentCity: driver.permanent_city || "",
              permanentPincode: driver.permanent_pincode || "",
              vehicle_id: driver.vehicle ? driver.vehicle.id : null,
              licenseExpiryDate: driver.licenseExpiryDate
                ? String(driver.licenseExpiryDate).split("T")[0]
                : "",
              supervisorId: driver.supervisorId ?? driver.supervisor?.id ?? "",
              supervisor: driver.supervisor || null,
              supervisorName:
                driver.supervisorName || driver.supervisor?.name || "",
              documents: driver.documents || [],
              uploadedDocuments:
                driver.documents
                  ?.filter(
                    (doc) =>
                      doc.type === "document" || doc.name !== "DriverImage",
                  )
                  .map((doc) => ({
                    type: doc.name,
                    image: `${config.baseURL}/upload/image?fileName=${doc.location}`,
                    file: null,
                  })) || [],
            },
          };
        }),
      );

      setDriversData(transformedData);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching drivers:", error);
      setIsLoading(false);

      if (error.response && error.response.status === 401) {
        navigate("/");
      }
    }
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  const handleFilterChange = (values) => {
    setFilterValues(values);
  };

  const handleCreateDriver = () => {
    setShowCreateForm(true);
    setExpandedRow(null);
  };

  const handleCloseForm = () => {
    setShowCreateForm(false);
  };

  const handleSubmitDriver = async (formData) => {
    // try {
    //   const response = await axios.post(
    //     `${config.baseURL}/driver`,
    //     formData,
    //     {
    //       withCredentials: true,
    //       headers: {
    //         'Content-Type': 'multipart/form-data',
    //       }
    //     }
    //   );

    //   if (response.status === 200 || response.status === 201) {
    // Refresh the drivers list
    await fetchDrivers();
    setShowCreateForm(false);
    //     console.log("New driver added:", response.data);
    //   }
    // } catch (error) {
    //   console.error("Error creating driver:", error);
    //   // Handle error (show notification, etc.)
    // }
  };

  const handleUpdateDriver = async () => {
    await fetchDrivers();
    setExpandedRow(null);
  };

  const handleDeleteDriver = async () => {
    if (expandedRow === null) return;

    const driverToDelete = driversData[expandedRow];

    try {
      const response = await axios.delete(`${config.baseURL}/driver`, {
        data: { id: driverToDelete.fullData.id },
        withCredentials: true,
      });

      if (response.status === 200) {
        // Refresh the drivers list
        await fetchDrivers();
        setExpandedRow(null);
        console.log("Driver deleted successfully");
      }
    } catch (error) {
      console.error("Error deleting driver:", error);
      // Handle error (show notification, etc.)
    }
  };

  const handleRowClick = (rowIndex) => {
    setExpandedRow(expandedRow === rowIndex ? null : rowIndex);
    setShowCreateForm(false);
  };

  const handleCloseExpandedForm = () => {
    setExpandedRow(null);
  };

  // Filter data based on search and filters
  const filteredData = useMemo(() => {
    let filtered = [...driversData];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((driver) => {
        return (
          driver.IDs.toLowerCase().includes(query) ||
          driver.Names.toLowerCase().includes(query) ||
          driver.Emails.toLowerCase().includes(query) ||
          driver["Phone No."].toLowerCase().includes(query) ||
          driver.Vehicles.toLowerCase().includes(query) ||
          driver["DL Numbers"].toLowerCase().includes(query)
        );
      });
    }

    // Apply vehicle filter
    if (filterValues.vehicles) {
      filtered = filtered.filter((driver) => {
        const vehicleId = driver.fullData.vehicle_id;
        return vehicleId && String(vehicleId) === String(filterValues.vehicles);
      });
    }

    return filtered;
  }, [searchQuery, filterValues, driversData]);

  useEffect(() => {
    if (openedDriverFromReport.current) return;
    const driverId = Number(searchParams.get("driverId"));
    if (!Number.isInteger(driverId) || driverId <= 0 || !filteredData.length) return;
    const index = filteredData.findIndex((row) => Number(row.fullData?.id) === driverId);
    if (index < 0) return;
    setExpandedRow(index);
    openedDriverFromReport.current = true;
  }, [searchParams, filteredData]);

  useEffect(() => {
    if (scrolledToDriver.current || isLoading || expandedRow == null) return;
    const driverId = searchParams.get("driverId");
    const row = driverId ? document.getElementById(`driver-row-${driverId}`) : null;
    if (!row) return;
    const timer = window.setTimeout(() => {
      row.scrollIntoView({ behavior: "smooth", block: "center" });
      scrolledToDriver.current = true;
    }, 200);
    return () => window.clearTimeout(timer);
  }, [expandedRow, isLoading, filteredData, searchParams]);

  // Custom expanded row renderer
  const renderExpandedRow = (row, rowIndex) => {
    return (
      <>
        <CreateDriverForm
          onClose={handleCloseExpandedForm}
          onDelete={handleDeleteDriver}
          onSubmit={handleUpdateDriver}
          initialData={row.fullData}
          isEdit={true}
        />
        <DriverBehaviorPanel driverId={row.fullData?.id} showDriver={false} />
      </>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: "100vh",
      }}
    >
      <Box>
        <TitleBar />
        <SchoolBusNavMenu />
        <SubTitle
          title={pageTitle}
          add={!organizationInactive ? (moduleLayout ? createAction?.labelKey : "+ Create Drivers") : ""}
          onAddClick={!organizationInactive ? handleCreateDriver : null}
        />
        <div className="table_container">
          {organizationInactive ? (
            <InactiveInventoryTable
              headers={headers}
              data={filteredData}
              loading={isLoading}
              emptyMessage={t("noDataFound")}
              itemLabel="drivers"
            />
          ) : (
            <>
          {showCreateForm && (
            <CreateDriverForm
              onClose={handleCloseForm}
              onSubmit={handleSubmitDriver}
              isEdit={false}
            />
          )}

          <SearchFilter
            searchPlaceholder="search"
            filters={filters}
            onSearchChange={handleSearchChange}
            onFilterChange={handleFilterChange}
            dataDownload={tableFeatures.downloadData !== false}
            downloadRows={filteredData}
            downloadColumns={headers
              .filter((header) => header.key !== "actions")
              .map((header) => ({
                key: header.key,
                label: header.value,
              }))}
            downloadFileName="school-drivers"
          />

          <Suspense fallback={<div className="loader"></div>}>
            <SchoolDynamicTable
              headers={headers}
              data={filteredData}
              loading={isLoading}
              expandedRow={
                tableFeatures.expandableRows === false ? null : expandedRow
              }
              onRowClick={
                tableFeatures.expandableRows === false
                  ? undefined
                  : handleRowClick
              }
              renderExpandedRow={
                tableFeatures.expandableRows === false
                  ? undefined
                  : renderExpandedRow
              }
              profileImageColumn={tableFeatures.profileImageColumn || "IDs"}
            />
          </Suspense>
            </>
          )}
        </div>
      </Box>
      <Footer />
    </Box>
  );
}
